import {
  Column,
  InlineNotification,
  SkeletonText,
  FlexGrid as Grid,
  Row,
  Tile,
  InlineLoading,
  Accordion,
  AccordionItem,
  Button,
} from '@carbon/react';

import { ReactNode } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useAgentDockWidth } from 'components/agent-chat';
import { EVAL_AGENT } from 'components/agent-chat/agents/eval-agent';
import { useHistory, useParams } from 'react-router-dom';
import { PackageResponse } from 'types/packages-api/responses';
import { PageHeader } from 'components/page-header';
import { Evaluations } from './evaluations';

import { PackagesSummaryTable } from './packages-summary';
import { PackagesMap } from './map/packages-map';
import NoMapSvg from 'images/srp-illustration-no-map.svg';
import { EmptyState } from 'components/empty-state';
import { ChevronLeft, ChevronRight, Edit } from '@carbon/react/icons';
import style from './page.module.scss';
import { Helmet } from 'react-helmet-async';
import { pageTitle } from 'utils/page-title';
import {
  accordionVisibleAtom,
  packageAtom,
  packageHrefAtom,
  summaryVisibleAtom,
} from './atoms';
import {
  assetsLayerAtom,
  developmentAreaLayerAtom,
  titleLayerAtom,
  wellsV2LayerAtom,
} from './map/atoms';
import { ProvideRootResource } from 'components/hydrate-atoms';
import { EventsPanel, EventsPanelOpenButton } from 'components/events-panel';
import { getMapDefaultConfig } from 'components/map/atoms';
import { atomWithStorage } from 'jotai/utils';

// The Eval 3 agent dock is `position: fixed` at z-index 5, and this page's
// PageHeader is `position: sticky` at z-index 7 spanning the full viewport
// width — so without reserving the dock's width the header paints straight over
// the top of the panel, burying Carbon's "Close the chat window" control and
// leaving no way to collapse the dock. Padding here (rather than raising the
// dock's z-index) keeps the fix local to this page and matches how the Document
// page hosts the same surface. `useAgentDockWidth` reports 0 while the dock is
// closed, so this is inert for everyone not using the agent.
const PackageContentWrapper = ({ children }: { children: ReactNode }) => {
  const agentWidth = useAgentDockWidth(EVAL_AGENT);

  return (
    <div style={agentWidth ? { paddingRight: agentWidth } : undefined}>
      {children}
    </div>
  );
};

const Header = ({
  packageData,
}: {
  packageData: PackageResponse | undefined;
}) => {
  const history = useHistory();

  return (
    <PageHeader
      // No breadcrumbs: the shared version switcher sits directly above this header, and the
      // sole non-root crumb was the package name — already shown as the `title` below (INS-11484).
      breadcrumbs={[]}
      stickyHeader
      title={packageData?.name || <SkeletonText heading width="200px" />}
      actions={
        <div className={style.actions}>
          {packageData && (
            <Button
              hasIconOnly
              kind="tertiary"
              size="md"
              renderIcon={Edit}
              iconDescription="Edit package"
              tooltipPosition="top"
              tooltipAlignment="center"
              onClick={() => history.push(`/packages/${packageData.id}/edit`)}
            />
          )}
          <EventsPanelOpenButton />
        </div>
      }
    />
  );
};

const Content = ({
  packageData,
}: {
  packageData: PackageResponse | undefined;
}) => {
  const [isSummaryVisible, setSummaryVisible] = useAtom(summaryVisibleAtom);
  const [isAccordionOpen, setIsAccordionOpen] = useAtom(accordionVisibleAtom);
  const handleSummaryClick = () => {
    setSummaryVisible((current) => !current);
  };
  const handleAccordionClick = () => {
    setIsAccordionOpen((current) => !current);
  };

  if (!packageData) return null;

  // jscpd:ignore-start -- the all-wells-off map config is repeated across about ten maps; consolidating them is its own change
  const packageMapConfigAtom = atomWithStorage(
    'insights-package-show-page-map-config-custom',
    getMapDefaultConfig({
      wellsV2: {
        checked: false,
        options: {
          surfaceHole: false,
          bottomHole: false,
          stick: false,
          survey: false,
          vertical: false,
          horizontal: false,
          horizontalPermit: false,
          horizontalDuc: false,
          horizontalProducing: false,
          horizontalShutIn: false,
          horizontalExpiredPermit: false,
          horizontalInactive: false,
          horizontalPrePermit: false,
          horizontalApd: false,
          horizontalProposal: false,
          horizontalPlanned: false,
          verticalPermit: false,
          verticalDuc: false,
          verticalProducing: false,
          verticalShutIn: false,
          verticalExpiredPermit: false,
          verticalInactive: false,
          verticalPrePermit: false,
          verticalApd: false,
          verticalProposal: false,
          verticalPlanned: false,
          permit: false,
          duc: false,
          producing: false,
          shutIn: false,
          expiredPermit: false,
          inactive: false,
          prePermit: false,
          apd: false,
          proposal: false,
          planned: false,
        },
      },
    })
  ); // jscpd:ignore-end

  return (
    <>
      <Grid condensed fullWidth className="bx--no-gutter">
        <Accordion align="end" size="lg" className={style.packagesAccordion}>
          <AccordionItem
            open={isAccordionOpen}
            title="Summary"
            onHeadingClick={handleAccordionClick}
          >
            <Row>
              <Column
                md={!isSummaryVisible ? 0 : 12}
                lg={!isSummaryVisible ? 0 : 3}
                className={style.packagesSummaryCol}
              >
                <PackagesSummaryTable packageData={packageData} />
              </Column>

              <Column
                md={!isSummaryVisible ? 12 : 12}
                lg={!isSummaryVisible ? 16 : 13}
                className={style.packagesMapCol}
              >
                <div className={style.closeAreabtn}>
                  <Button
                    renderIcon={!isSummaryVisible ? ChevronRight : ChevronLeft}
                    kind="ghost"
                    hasIconOnly
                    iconDescription={
                      isSummaryVisible
                        ? `Hide summary details`
                        : `Show summary details`
                    }
                    tooltipAlignment="start"
                    tooltipPosition="top"
                    size="sm"
                    className={style.closePanelBtn}
                    onClick={handleSummaryClick}
                  />
                </div>
                {isAccordionOpen && packageData.boundingBox ? (
                  <ProvideRootResource>
                    <PackagesMap
                      packageMapId={String(isSummaryVisible)}
                      boundingBox={packageData.boundingBox}
                      mapSources={packageData.resources.maps}
                      customMapConfigAtom={packageMapConfigAtom}
                      hideWellsLayer={true}
                      layerAtoms={{
                        assets: assetsLayerAtom,
                        devAreas: developmentAreaLayerAtom,
                        title: titleLayerAtom,
                        wellsV2: wellsV2LayerAtom,
                      }}
                    />
                  </ProvideRootResource>
                ) : (
                  <Tile className={style.emptyTileMessage}>
                    <EmptyState
                      icon={NoMapSvg}
                      iconClassName={style.emptyMapIcon}
                      headerText="No map available"
                      helperText="Currently Insights doesn't have enough data"
                    />
                  </Tile>
                )}
              </Column>
            </Row>
          </AccordionItem>
        </Accordion>
      </Grid>
      <Evaluations href={packageData.resources.evaluations.href} />
    </>
  );
};

const LoadPackage = ({
  render,
}: {
  render: (packageData: PackageResponse) => JSX.Element;
}) => {
  const packageRequest = useAtomValue(packageAtom);
  if (packageRequest.state === 'loading') return <InlineLoading />;
  if (
    packageRequest.state === 'hasData' &&
    packageRequest.data &&
    'error' in packageRequest.data
  ) {
    return (
      <InlineNotification
        kind="error"
        title={packageRequest.data.error}
        lowContrast
      />
    );
  }

  if (
    packageRequest.state === 'hasData' &&
    packageRequest.data &&
    !('error' in packageRequest.data)
  )
    return render(packageRequest.data);

  return null;
};

const EventsContent = ({ packageData }: { packageData: PackageResponse }) => {
  return (
    <EventsPanel
      header={packageData.name}
      title="Package"
      helperText="Context allows you to share information about a package."
      eventsHref={packageData.resources.events.href}
      commentsHref={packageData.resources.comments.href}
    />
  );
};

const ShowPackagePage = () => {
  const params = useParams<{ id: string }>();
  const packageHref = `/packages/${params.id}`;

  return (
    <ProvideRootResource initialValues={[[packageHrefAtom, packageHref]]}>
      <LoadPackage
        render={(packageData: PackageResponse) => {
          return (
            <>
              <Helmet>
                <title>{pageTitle(`${packageData.name}`)}</title>
              </Helmet>
              {/* Padded by the agent dock's width so the panel does not overlap the content. */}
              <PackageContentWrapper>
                <Header packageData={packageData} />
                <Content packageData={packageData} />
              </PackageContentWrapper>
              <EventsContent packageData={packageData} />
            </>
          );
        }}
      />
    </ProvideRootResource>
  );
};

export { ShowPackagePage, LoadPackage };
